import {
  type ActiveElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  type ChartEvent,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import classNames from "classnames";
import * as React from "react";

import { ChartDisplayType } from "@/shared/domain/metrics";
import { nsToDateLabel } from "@/shared/time/time";

import styles from "./MetricChart.module.css";

// Register only the Chart.js pieces this chart uses (matches the explicit
// registration style in PlotRenderer.ts). Registration is global + idempotent.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// Canvas can't resolve CSS custom properties, so read the concrete color off
// the element at draw time, with a fallback matching --primary-main.
const FALLBACK_COLOR = "rgb(2, 136, 209)";

export interface PlotDatum {
  /** Bucket start, Unix-epoch nanoseconds. `bigint` to keep full ns precision:
   *  on the category x-axis Chart.js never reads these (it scales off
   *  `label`), they only ride along for the click → drill-down query. */
  start_time: bigint;
  /** Bucket end, Unix-epoch nanoseconds. See `start_time`. */
  end_time: bigint;
  /** Absent on placeholder rows; present on real aggregate buckets. */
  value?: number;
  /** Pre-formatted x-axis label (short ISO date). Category x-axis only. */
  label: string;
  /** Pre-formatted tooltip title (start–end range). */
  tooltipTitle: string;
  /** Linear x-axis only: this point's x coordinate in Unix-epoch
   *  MILLISECONDS. Chart.js scales numerically off this, so points sit at
   *  their real elapsed distance from one another rather than at even
   *  positional intervals. Ignored on the category axis. */
  x?: number;
  /** Raw mode only: the single session this point came from. */
  session_id?: string;
}

/**
 * How the x-axis maps a point to a position.
 *
 * `category` — evenly spaced positions labelled by {@link PlotDatum.label}.
 * The AGGREGATED path: its buckets are uniform and gap-filled, so even
 * spacing IS time-proportional.
 *
 * `linear` — numeric epoch-ms positions from {@link PlotDatum.x}. The RAW
 * path: one point per session at whatever moment that session ended, with no
 * uniform bucket to synthesize gaps from. Category spacing would draw a
 * three-year-old session and yesterday's an equal distance apart.
 */
export type ChartXScale = "category" | "linear";

export interface MetricChartViewProps {
  /** Pre-computed series: gap-filled with formatted labels for the category
   *  axis, or sorted ascending by `x` for the linear one. */
  series: PlotDatum[];
  metricName: string;
  /** Unit of measure for the plotted values; titles the y-axis. Omit if unitless. */
  unit?: string | null;
  /** Line (default) or bar rendering. Bar is only offered on the category
   *  axis — Chart.js sizes bars from the smallest inter-point interval, which
   *  on an irregular linear axis collapses them to hairlines. */
  display?: ChartDisplayType;
  /** Positional (default) or time-proportional x-axis. */
  xScale?: ChartXScale;
  /** Fired when a real (non-placeholder) datum is clicked. */
  onDatumClick?: (datum: PlotDatum) => void;
}

/**
 * The dataset's `data` array for `xScale`.
 *
 * Category: bare y values, positioned by their index against `labels`.
 * Linear: `{x, y}` pairs, positioned numerically by epoch-ms `x`. A datum
 * with no value plots as `NaN`, which Chart.js skips — the linear path only
 * ever carries real points, so this is the type-level floor rather than a
 * case the raw series produces.
 */
function datasetData(
  series: PlotDatum[],
  xScale: ChartXScale,
): (number | null)[] | { x: number; y: number }[] {
  return xScale === "linear"
    ? series.map((d) => ({ x: d.x ?? Number.NaN, y: d.value ?? Number.NaN }))
    : series.map((d) => d.value ?? null);
}

/** Category labels, or `undefined` on the linear axis (which reads its
 *  positions off the data instead, and renders its own tick text). */
function chartLabels(
  series: PlotDatum[],
  xScale: ChartXScale,
): string[] | undefined {
  return xScale === "linear" ? undefined : series.map((d) => d.label);
}

/**
 * Chart presentation layer for metrics.
 *
 * Renders a Chart.js canvas from an already-computed {@link PlotDatum} series,
 * or an empty-state message when the series is empty.
 *
 * Does no data fetching or transformation — that belongs in the {@link MetricChart} wrapper.
 */
export function MetricChartView({
  series,
  metricName,
  unit = null,
  display = "line",
  xScale = "category",
  onDatumClick,
}: MetricChartViewProps) {
  const chartRef = React.useRef<Chart | null>(null);

  // The chart is created once per `display` type and then mutated in place (see
  // the update effect below) as the series / metric name change, so it — and
  // its option callbacks — outlive those data changes. The callbacks therefore
  // read the latest values through refs instead of closing over stale ones.
  const clickRef = React.useRef(onDatumClick);
  clickRef.current = onDatumClick;
  const seriesRef = React.useRef(series);
  seriesRef.current = series;
  const metricNameRef = React.useRef(metricName);
  metricNameRef.current = metricName;
  const unitRef = React.useRef(unit);
  unitRef.current = unit;

  // Build (and tear down) the chart as the <canvas> mounts and whenever
  // `display` or `xScale` changes — Chart.js can't switch an existing chart's
  // type or a scale's type, so those are the cases that need a fresh instance.
  // Series / metric name changes never reach here; they flow through
  // chart.update() below. The canvas is absent in the empty state, so this
  // also disposes the chart when the series empties out.
  const setCanvas = React.useCallback(
    (canvas: HTMLCanvasElement | null) => {
      chartRef.current?.destroy();
      chartRef.current = null;
      if (!canvas) {
        return;
      }

      const color =
        getComputedStyle(canvas).getPropertyValue("--primary-main").trim() ||
        FALLBACK_COLOR;

      chartRef.current = new Chart(canvas, {
        type: display === "bar" ? "bar" : "line",
        data: {
          labels: chartLabels(seriesRef.current, xScale),
          datasets: [
            {
              label: metricNameRef.current,
              data: datasetData(seriesRef.current, xScale),
              borderColor: color,
              backgroundColor: color,
              // Connect across missing buckets (rendered as null) instead of
              // leaving gaps; fillMissingBuckets still keeps the x-axis spacing
              // time-proportional.
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: "index", intersect: false },
          onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
            const index = elements[0]?.index;
            if (index === undefined) {
              return;
            }
            const datum = seriesRef.current[index];
            if (datum && datum.value !== undefined) {
              clickRef.current?.(datum);
            }
          },
          scales: {
            x:
              xScale === "linear"
                ? {
                    type: "linear",
                    ticks: {
                      autoSkip: true,
                      maxRotation: 0,
                      // Linear ticks land on Chart.js "nice numbers" in epoch
                      // ms rather than on calendar boundaries, so each is
                      // rendered as the date it falls on. (Every dashboard
                      // range is at least 30 days, so date resolution is the
                      // right granularity.) The ×1e6 is ms → ns for the shared
                      // formatter.
                      callback: (value: string | number) =>
                        nsToDateLabel(Number(value) * 1_000_000),
                    },
                  }
                : { ticks: { autoSkip: true, maxRotation: 0 } },
            y: {
              beginAtZero: true,
              title: {
                display: Boolean(unitRef.current),
                text: unitRef.current ?? "",
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const index = items[0]?.dataIndex;
                  if (index === undefined) {
                    return "";
                  }
                  return seriesRef.current[index]?.tooltipTitle ?? "";
                },
                label: (item) => {
                  const y = item.parsed.y;
                  if (typeof y !== "number" || Number.isNaN(y)) {
                    return "—";
                  }
                  return unitRef.current
                    ? `${String(y)} ${unitRef.current}`
                    : String(y);
                },
              },
            },
          },
        },
      });
    },
    [display, xScale],
  );

  // Push series / metric-name / unit changes into the existing chart rather than
  // rebuilding it (Chart.js's recommended path for data updates). The tooltip
  // callback reads the unit off its ref, but the y-axis title is baked into the
  // options object at build time, so it has to be reassigned here.
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.data.labels = chartLabels(series, xScale);
    const [dataset] = chart.data.datasets;
    if (dataset) {
      dataset.data = datasetData(series, xScale);
      dataset.label = metricName;
    }
    // `scales.y` is typed as a union over every scale kind (linear, time,
    // radial…), so `title` isn't reachable without narrowing to the cartesian
    // shape we actually build above.
    const yScale = chart.options.scales?.y as
      | { title?: { display?: boolean; text?: string } }
      | undefined;
    if (yScale?.title) {
      yScale.title.display = Boolean(unit);
      yScale.title.text = unit ?? "";
    }
    chart.update();
  }, [series, metricName, xScale, unit]);

  if (series.length === 0) {
    return (
      <p className={styles.empty}>No data points in the selected window.</p>
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.clickable]: Boolean(onDatumClick),
      })}
    >
      <canvas ref={setCanvas} role="img" aria-label={`${metricName} chart`} />
    </div>
  );
}
